import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { exercisePopupsService } from '../services/exercisePopupsService';
import {
  ExercisePopupStats,
  ExerciseTypeInfo,
  PopupTypeStats,
  EMPTY_EXERCISE_POPUP_STATS,
  resolveTypeTitle,
} from '../types/exercisePopups';

interface Props {
  exerciseTypes: ExerciseTypeInfo[];
}

// Chart chrome (single-hue marks; text always in text tokens, never the series color)
const MARK = '#2a78d6';
const MARK_SOFT = '#9ec5f4';
const GRID = '#e1e0d9';
const BASELINE = '#c3c2b7';

const formatPct = (num: number, den: number): string =>
  den > 0 ? `${Math.round((num / den) * 100)}%` : '–';

const formatRatio = (num: number, den: number): string =>
  den > 0 ? (num / den).toFixed(1) : '–';

const formatDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '–';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Sum per-type counters into one row (type_id kept as a label only). */
const sumStats = (rows: PopupTypeStats[], typeId = 'all'): PopupTypeStats =>
  rows.reduce<PopupTypeStats>(
    (acc, row) => ({
      type_id: typeId,
      presented: acc.presented + row.presented,
      solved: acc.solved + row.solved,
      interrupted: acc.interrupted + row.interrupted,
      correct: acc.correct + row.correct,
      wrong: acc.wrong + row.wrong,
      first_try_correct: acc.first_try_correct + row.first_try_correct,
      solve_ms_total: acc.solve_ms_total + row.solve_ms_total,
    }),
    {
      type_id: typeId,
      presented: 0,
      solved: 0,
      interrupted: 0,
      correct: 0,
      wrong: 0,
      first_try_correct: 0,
      solve_ms_total: 0,
    }
  );

/** YYYY-MM-DD for each of the last `days` local days, oldest first. */
const lastDays = (days: number): string[] => {
  const out: string[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    out.push(`${d.getFullYear()}-${m}-${day}`);
  }
  return out;
};

interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, hint }) => (
  <Box
    sx={{
      flex: '1 1 160px',
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" component="div" sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * Statistics dashboard for the pop-up exercises: how often they appeared, how many the
 * child solved, how many wrong taps it took per exercise type, and the daily trend.
 * Data comes from devices/{id}/games/popupStats (contracts/firebase-schema.md §10.1),
 * written by the tablet and served by the backend.
 */
const ExerciseStatsSection: React.FC<Props> = ({ exerciseTypes }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { selectedDevice, showSnackbar } = useDevice();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ExercisePopupStats>(EMPTY_EXERCISE_POPUP_STATS);
  const [days, setDays] = useState<7 | 14 | 30>(14);
  const [hoverDay, setHoverDay] = useState<string | null>(null);

  const typeName = useCallback(
    (typeId: string) =>
      resolveTypeTitle(exerciseTypes.find((type) => type.id === typeId), i18n.language, typeId),
    [exerciseTypes, i18n.language]
  );

  const loadStats = useCallback(async () => {
    if (!selectedDevice?.id) return;
    try {
      setLoading(true);
      const data = await exercisePopupsService.getStats(selectedDevice.id, days, 50);
      setStats(data);
    } catch (error) {
      console.error('Failed to load exercise statistics:', error);
      showSnackbar(t('exerciseStats.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, days, showSnackbar, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const overall = useMemo(() => sumStats(stats.totals), [stats.totals]);

  // Daily series: solved + presented per day over the selected window (zero-filled)
  const daily = useMemo(() => {
    const byDate = new Map<string, { solved: number; presented: number }>();
    for (const row of stats.daily) {
      const cur = byDate.get(row.date) || { solved: 0, presented: 0 };
      cur.solved += row.solved;
      cur.presented += row.presented;
      byDate.set(row.date, cur);
    }
    return lastDays(days).map((date) => ({ date, ...(byDate.get(date) || { solved: 0, presented: 0 }) }));
  }, [stats.daily, days]);
  const dailyMax = Math.max(1, ...daily.map((d) => d.presented));

  // Per-type rows sorted by how often the type appeared
  const typeRows = useMemo(
    () => [...stats.totals].sort((a, b) => b.presented - a.presented),
    [stats.totals]
  );
  const maxMistakes = Math.max(
    0.1,
    ...typeRows.map((row) => (row.solved > 0 ? row.wrong / row.solved : 0))
  );

  const dateLabel = (date: string) => {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  };

  if (!selectedDevice) return null;

  const empty = !loading && overall.presented === 0;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h6">{t('exerciseStats.title')}</Typography>
          <IconButton
            size="small"
            onClick={loadStats}
            disabled={loading}
            aria-label={t('exerciseStats.refresh')}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          {loading && <CircularProgress size={16} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('exerciseStats.description')}
        </Typography>

        {empty ? (
          <Typography variant="body2" color="text.secondary">
            {t('exerciseStats.empty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* KPI row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StatTile
                label={t('exerciseStats.kpiSolved')}
                value={String(overall.solved)}
                hint={t('exerciseStats.kpiSolvedHint', { presented: overall.presented })}
              />
              <StatTile
                label={t('exerciseStats.kpiFirstTry')}
                value={formatPct(overall.first_try_correct, overall.correct)}
                hint={t('exerciseStats.kpiFirstTryHint', {
                  firstTry: overall.first_try_correct,
                  rounds: overall.correct,
                })}
              />
              <StatTile
                label={t('exerciseStats.kpiMistakes')}
                value={formatRatio(overall.wrong, overall.solved)}
                hint={t('exerciseStats.kpiMistakesHint', { wrong: overall.wrong })}
              />
              <StatTile
                label={t('exerciseStats.kpiAvgTime')}
                value={formatDuration(overall.solved > 0 ? overall.solve_ms_total / overall.solved : 0)}
                hint={t('exerciseStats.kpiAvgTimeHint')}
              />
            </Box>

            {/* Daily trend */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">{t('exerciseStats.trendTitle')}</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={days}
                  onChange={(_, v) => v && setDays(v)}
                >
                  <ToggleButton value={7}>{t('exerciseStats.range7')}</ToggleButton>
                  <ToggleButton value={14}>{t('exerciseStats.range14')}</ToggleButton>
                  <ToggleButton value={30}>{t('exerciseStats.range30')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: MARK }} />
                  <Typography variant="caption" color="text.secondary">{t('exerciseStats.legendSolved')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: MARK_SOFT }} />
                  <Typography variant="caption" color="text.secondary">{t('exerciseStats.legendPresented')}</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '2px',
                  height: 140,
                  borderBottom: `1px solid ${BASELINE}`,
                  backgroundImage: `linear-gradient(${GRID} 1px, transparent 1px)`,
                  backgroundSize: '100% 50%',
                  position: 'relative',
                }}
                onMouseLeave={() => setHoverDay(null)}
              >
                {daily.map((d) => {
                  const presentedH = (d.presented / dailyMax) * 100;
                  const solvedH = (d.solved / dailyMax) * 100;
                  const active = hoverDay === d.date;
                  return (
                    <Tooltip
                      key={d.date}
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ fontSize: 12 }}>
                          <div>{dateLabel(d.date)}</div>
                          <div>{t('exerciseStats.tooltipSolved', { count: d.solved })}</div>
                          <div>{t('exerciseStats.tooltipPresented', { count: d.presented })}</div>
                        </Box>
                      }
                    >
                      <Box
                        onMouseEnter={() => setHoverDay(d.date)}
                        sx={{
                          flex: 1,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          cursor: 'default',
                          bgcolor: active ? 'action.hover' : 'transparent',
                        }}
                      >
                        <Box sx={{ position: 'relative', width: '60%', maxWidth: 24, height: '100%' }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              width: '100%',
                              height: `${presentedH}%`,
                              bgcolor: MARK_SOFT,
                              borderRadius: '4px 4px 0 0',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              width: '100%',
                              height: `${solvedH}%`,
                              bgcolor: MARK,
                              borderRadius: '4px 4px 0 0',
                            }}
                          />
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{dateLabel(daily[0].date)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('exerciseStats.axisMax', { count: dailyMax })}
                </Typography>
                <Typography variant="caption" color="text.secondary">{dateLabel(daily[daily.length - 1].date)}</Typography>
              </Box>
            </Box>

            {/* By exercise type */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('exerciseStats.byTypeTitle')}
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('exerciseStats.colType')}</TableCell>
                      <TableCell align="right">{t('exerciseStats.colPresented')}</TableCell>
                      <TableCell align="right">{t('exerciseStats.colSolved')}</TableCell>
                      <TableCell align="right">{t('exerciseStats.colFirstTry')}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>{t('exerciseStats.colMistakes')}</TableCell>
                      <TableCell align="right">{t('exerciseStats.colAvgTime')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {typeRows.map((row) => {
                      const perSolved = row.solved > 0 ? row.wrong / row.solved : 0;
                      return (
                        <TableRow key={row.type_id}>
                          <TableCell>{typeName(row.type_id)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.presented}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {row.solved}
                            {row.interrupted > 0 && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {t('exerciseStats.interruptedSuffix', { count: row.interrupted })}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatPct(row.first_try_correct, row.correct)}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              arrow
                              title={t('exerciseStats.mistakesTooltip', {
                                wrong: row.wrong,
                                solved: row.solved,
                              })}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 12, bgcolor: GRID, borderRadius: '0 4px 4px 0', overflow: 'hidden' }}>
                                  <Box
                                    sx={{
                                      width: `${Math.min(100, (perSolved / maxMistakes) * 100)}%`,
                                      height: '100%',
                                      bgcolor: MARK,
                                      borderRadius: '0 4px 4px 0',
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                  {formatRatio(row.wrong, row.solved)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatDuration(row.solved > 0 ? row.solve_ms_total / row.solved : 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* Recent results */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('exerciseStats.recentTitle')}
              </Typography>
              {stats.recent.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('exerciseStats.recentEmpty')}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('exerciseStats.colTime')}</TableCell>
                        <TableCell>{t('exerciseStats.colType')}</TableCell>
                        <TableCell>{t('exerciseStats.colExercise')}</TableCell>
                        <TableCell>{t('exerciseStats.colOutcome')}</TableCell>
                        <TableCell align="right">{t('exerciseStats.colWrongTaps')}</TableCell>
                        <TableCell align="right">{t('exerciseStats.colDuration')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.recent.slice(0, 20).map((event) => (
                        <TableRow key={event.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(event.finished_at).toLocaleString(i18n.language)}
                          </TableCell>
                          <TableCell>{typeName(event.type_id)}</TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>{event.exercise_id}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={event.outcome === 'solved' ? 'success' : 'default'}
                              variant={event.outcome === 'solved' ? 'filled' : 'outlined'}
                              label={t(`exerciseStats.outcome.${event.outcome}`)}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{event.wrong}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {event.outcome === 'solved' ? formatDuration(event.solve_ms) : '–'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ExerciseStatsSection;
